import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantContextValue } from 'src/context/TenantContextProvider';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { orgRestaurantBranches } from 'src/services/apis/tenantRequests';
import { authClient } from 'src/services/auth/client';
import { clearAccessToken } from 'src/services/auth/tokenStore';
import { QUERIES } from 'src/utils/react-query';
import { pickDefaultBranchId, switchScope } from './switchContext';

export type ContextSwitch = {
  selectOrganization: (organizationId: string, slug: string) => Promise<void>;
  goRestaurant: (restaurantId: string) => Promise<void>;
  goBranch: (branchId: string) => void;
  /** Message from the last failed organization switch; the context was left untouched. */
  switchError: string | null;
  clearSwitchError: () => void;
};

/**
 * The org/restaurant/branch switching rules, lifted out of the header panels they used to
 * live in so they can be tested without rendering a panel. switchContext.ts keeps the pure
 * scope arithmetic; this hook owns the side effects around it.
 */
export function useContextSwitch(): ContextSwitch {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { context, setContext } = useTenantContextValue();
  const { canViewAllBranches } = useAuthPermissions();
  const [switchError, setSwitchError] = useState<string | null>(null);

  const goBranch = useCallback(
    (branchId: string) => setContext(switchScope(context, { branchId }, { canAggregate: canViewAllBranches })),
    [context, setContext, canViewAllBranches]
  );

  const goRestaurant = useCallback(
    async (restaurantId: string) => {
      if (restaurantId === context.restaurantId) {
        return;
      }
      let nextBranches: { id: string }[] | undefined;
      try {
        const response = await orgRestaurantBranches({ orgSlug: context.orgSlug, restaurantId });
        nextBranches = response?.data;
      } catch {
        // Fall through to pickDefaultBranchId's empty-list behaviour: owner/admin land on
        // the aggregate, a member lands on "" and ContextLoader re-resolves defaults.
        nextBranches = undefined;
      }
      setContext(
        switchScope(
          context,
          { restaurantId, branchId: pickDefaultBranchId(nextBranches, canViewAllBranches) },
          { canAggregate: canViewAllBranches }
        )
      );
    },
    [context, setContext, canViewAllBranches]
  );

  const selectOrganization = useCallback(
    async (organizationId: string, slug: string) => {
      setSwitchError(null);
      try {
        // The active organization is a session-side fact: it decides the org claims in the
        // next minted JWT, so it must land before anything navigates.
        const result = await authClient.organization.setActive({ organizationId });
        if (result?.error) {
          setSwitchError(result.error.message ?? 'Unknown error');
          return;
        }
      } catch (error) {
        // Stop here on purpose. Clearing the token and navigating anyway is what the
        // original did, and it left the session claiming one org while rendering another.
        setSwitchError(error instanceof Error ? error.message : 'Unknown error');
        return;
      }

      try {
        clearAccessToken();
        // Authoritative read: refresh the session_data cookie view before navigating.
        await authClient.getSession({ query: { disableCookieCache: true } });
        // Onboarding status is org-scoped; drop the stale cache so the root sees the new
        // org's status rather than a cached look-alike.
        await queryClient.invalidateQueries({ queryKey: [QUERIES.MEMBERSHIPS] });
        await queryClient.invalidateQueries({ queryKey: [QUERIES.ONBOARDING_STATUS] });
      } catch {
        // setActive has already landed server-side: the session now claims the new
        // organization regardless of what happens here. Completing the switch client-side
        // is the safe direction from this point on — leaving it half-done would render the
        // old org while the next JWT carries the new one's claims.
      }
      setContext(switchScope(context, { orgSlug: slug }));
      navigate('/');
    },
    [context, setContext, navigate, queryClient]
  );

  const clearSwitchError = useCallback(() => setSwitchError(null), []);

  return { selectOrganization, goRestaurant, goBranch, switchError, clearSwitchError };
}
